import { SortDirection } from '@bp/planung-graphql-types';
import { createContext, ReactNode, useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { v4 } from 'uuid';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { StoreHandler } from '../../../stores/StoreHandler';
import { useTimetableVersionLessonsQuery } from '../../../types/planung-graphql-client-defs';
import { lessonResponseToTableType } from '../utils/lessonResponseToTableType';
import { LessonTableType } from './Table/TimetableVersionLessonsTable';
import { TimetableVersionLessonStore } from './TimetableVersionLessonStore';

const LessonStoreContext = createContext<TimetableVersionLessonStore>({} as TimetableVersionLessonStore);
const timetableVersionLessonStoreHandler = new StoreHandler<TimetableVersionLessonStore>();

export const TimetableVersionLessonProvider = ({
  children,
  organizationUuid,
  schoolYearUuid,
}: {
  children: ReactNode | undefined;
  organizationUuid: string;
  schoolYearUuid: string;
}) => {
  const { versionUuid } = useParams();

  const store = timetableVersionLessonStoreHandler.register(versionUuid ?? v4(), () => {
    return new TimetableVersionLessonStore();
  });

  const { subjectData, classesData, teacherData, roomsData, subjectContainerData } = useLoadBasicData({
    pause: !versionUuid,
  });

  const [{ data: queryLessonsData }] = useTimetableVersionLessonsQuery({
    variables: {
      lessonWhere: {
        versions_SOME: {
          uuid: versionUuid,
        },
        organization: {
          uuid: organizationUuid,
        },
        schoolYear: {
          uuid: schoolYearUuid,
        },
      },
      options: {
        sort: [
          { classSort: SortDirection.Asc },
          { subjectName: SortDirection.Asc },
          { teachersSort: SortDirection.Asc },
        ],
      },
    },
    requestPolicy: 'network-only',
    pause: !versionUuid || versionUuid === '',
  });

  const lessonsData: LessonTableType[] = lessonResponseToTableType({
    queryLessonsData: queryLessonsData?.lessons,
    deputateByTeachersData: [], // TODO: add from backend-resolver
    versionContext: true,
    isTeachingBlock: false,
    subjectsData: subjectData?.subjects,
    subjectContainerData: subjectContainerData?.subjectContainers,
  });

  useEffect(() => {
    if (store.lessons.size === 0) {
      store.setLessons(lessonsData);

      store.setBasicData({
        subjects: subjectData?.subjects,
        classes: classesData?.classes,
        teachers: teacherData?.people,
        rooms: roomsData?.rooms,
      });
    }
  }, [store, lessonsData, subjectData?.subjects, classesData?.classes, teacherData?.people, roomsData?.rooms]);

  return <LessonStoreContext.Provider value={store}>{children}</LessonStoreContext.Provider>;
};

export const useLessonStore = () => useContext(LessonStoreContext);
